import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, catchError, throwError, finalize, map } from "rxjs";
import { AuthService } from "./auth.service";
import { MessageService } from "primeng/api";
import { EncryptionService } from "./encryption.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { MessageManagerService } from "./messagemanagerService.service";
import { CommonPopupService } from "./common-popup.service";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";



@Injectable({
    providedIn: 'root'
})
export class InterceptorService implements HttpInterceptor {
    constructor(
        private authService: AuthService,
        private messageService: MessageManagerService,
        private router: Router,
        private encryptionService: EncryptionService,
        private ngxLoader: NgxUiLoaderService,
        private commonPopupService: CommonPopupService,
        private translate: TranslateService
    ) { }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const isTranslationRequest = this.isTranslationRequest(request.url);
        if (!isTranslationRequest) {
            this.ngxLoader.start();
        }

        // Add authentication token if available
        const token = this.authService.getUserToken();
        if (token) {
            request = request.clone({
                setHeaders: {
                    Authorization: `Bearer ${token}`,
                },
            });
        }

        let payload;
        // Intercept only POST requests
        if (request.method === "POST") {
            // Encrypt request payload
            if (request.body instanceof FormData) {
                payload = request.body;
            } else {
                payload = this.encryptionService.encryptPayload(
                    JSON.stringify(request.body)
                );
            }
            request = request.clone({
                body: payload,
            });
        }

        return next.handle(request).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    // Decrypt i18n JSON responses as well as normal API responses
                    const needsDecrypt =
                        event.url?.includes('/i18n/') ||
                        event.url?.endsWith('.json') ||
                        event.url?.includes('en.json') ||
                        event.url?.includes('fr.json');

                    if (needsDecrypt) {
                        const decryptedResponse = this.encryptionService.decryptResponse(event.body) as any;
                        const decryptedBody = decryptedResponse ? JSON.parse(decryptedResponse) : event.body;
                        return event.clone({ body: decryptedBody });
                    }

                    // Default behaviour for non-i18n responses
                    const decryptedResponse = this.encryptionService.decryptResponse(event.body) as any;
                    const decryptedBody = decryptedResponse ? JSON.parse(decryptedResponse) : event.body;
                    return event.clone({ body: decryptedBody });
                }
                return event;
            }),
            catchError((error: HttpErrorResponse) => {
                if (this.isTranslationRequest(error.url)) {
                    return throwError(() => error);
                }

                let decryptedErrorBody = error.error;

                // Decrypt if encrypted format exists
                if (error?.error?.Payload && error?.error?.PublicKey) {
                    try {
                        const decrypted = this.encryptionService.decryptResponse(error.error);
                        decryptedErrorBody = decrypted ? JSON.parse(decrypted) : error.error;
                    } catch (e) {
                        decryptedErrorBody = error.error;
                    }
                }

                // Create NEW error with decrypted body
                const decryptedError = new HttpErrorResponse({
                    error: decryptedErrorBody,
                    status: error.status,
                    statusText: error.statusText,
                    url: error.url || undefined,
                });

                this.handleErrorResponse(decryptedError);
                return throwError(() => decryptedError);
            }),
            finalize(() => {
                if (!isTranslationRequest) {
                    this.ngxLoader.stop();
                }
            })
        );
    }

    private isTranslationRequest(url: string | null | undefined): boolean {
        if (!url) {
            return false;
        }
        const lower = url.toLowerCase();
        return (
            lower.includes('/i18n/') ||
            lower.includes('/portal/i18n/') ||
            lower.includes('blob.core') ||
            lower.includes('/assets/i18n/') ||
            (lower.endsWith('.json') && !lower.includes('/api/'))
        );
    }

    private handleErrorResponse(error: HttpErrorResponse): void {
        const body = error.error;
        const serverMessage = this.resolveApiErrorMessage(body);
        if (error.status === 400) {
            // Handle field-level validation errors
            if (body?.errors && typeof body.errors === 'object' && Object.keys(body.errors).length > 0) {
                const messages = Object.values(body.errors)
                    .flat()
                    .filter((m): m is string => typeof m === 'string');

                if (messages.length > 0) {
                    messages.forEach(msg => {
                        this.messageService.add({
                            severity: 'error',
                            summary: this.translate.instant("COMMON.ERROR"),
                            detail: msg,
                        });
                    });
                    return;
                }
            }

            //this.commonPopupService.notifyLockedError(error.error?.message);
            if (serverMessage.toLowerCase().includes('maximum otp limit')) {
                this.messageService.add({
                    severity: "error",
                    summary: this.translate.instant("COMMON.ERROR"),
                    detail: this.translate.instant("COMMON.MSG_MAX_OTP_LIMIT_REACHED"),
                });
                // Show the error message for 3 seconds before redirecting
                setTimeout(() => {
                    this.router.navigate(["/auth/sign-in"]);
                }, 3000);
            }
            else {
                // Check if we should show toast for this error
                if (this.shouldShowToast(serverMessage)) {
                        const errorMessage = `${serverMessage}`;
                    this.messageService.add({
                        severity: "error",
                        summary: this.translate.instant("COMMON.ERROR"),
                        detail: errorMessage,
                    });
                }
            }
        }
        else if (error.status === 401) {
            // Route to /auth/sign-in for 401 errors
            const message = this.peekApiErrorMessage(body) ?? this.translate.instant("COMMON.AUTHENTICATION_FAILED");

            // Only show toast if we should show it for this error
            if (this.shouldShowToast(message)) {
                this.messageService.add({
                    severity: "error",
                    summary: this.translate.instant("COMMON.UNAUTHORIZED"),
                    detail: message,
                });
            }
            
         this.authService.logout('unauthorized');
        } else if (error.status === 403) {
            // Handle 403 errors with toast message
            this.messageService.add({
                severity: "error",
                summary: this.translate.instant("COMMON.FORBIDDEN"),
                detail: this.translate.instant("COMMON.MSG_FORBIDDEN_ACTION"),
            });
        } else if (error.status === 423) {
              Title = "";
            if(error.error.errorCode=="PermanentBlock")
                var Title = this.translate.instant("COMMON.ACCOUNT_PERMANENTLY_LOCKED");
            else
                var Title = this.translate.instant("COMMON.ACCOUNT_TEMPORARILY_LOCKED");

            this.commonPopupService.notifyLockedError(Title, this.resolveApiErrorMessage(body));
        } else if (error.status === 406) {
            // Handle 423 Locked errors with common popup
            this.commonPopupService.notifyLockedError(this.translate.instant("COMMON.AUTHENTICATION_FAILED"), this.resolveApiErrorMessage(body));
        }else {
            if (error?.url?.includes('getDashboardTranstionHistory') || error?.url?.includes('getwalletsbalance') || error?.url?.includes('getwallets')) {
                return;
            }

            if (this.peekApiErrorMessage(body) === null) {
                this.messageService.add({
                    severity: "error",
                    summary: this.translate.instant("COMMON.ERROR"),
                    detail: this.translate.instant("INTERCEPTOR.GENERIC_ERROR"),
                });
            }
            else {
                const errorMessage = `${this.translate.instant("COMMON.ERROR")}: ${serverMessage}`;
                this.messageService.add({
                    severity: "error",
                    summary: this.translate.instant("COMMON.ERROR"),
                    detail: errorMessage,
                });
            }
        }
    }

    private isFrenchSelected(): boolean {
        const lang = this.translate.currentLang || localStorage.getItem('lang') || 'en';
        return lang === 'fr' || lang.startsWith('fr-') || lang.startsWith('fr_');
    }

    /**
     * Returns the API exception text for the active UI language (MessageFr / MessageEn),
     * or null when the body has no usable message fields.
     */
    private peekApiErrorMessage(body: any): string | null {
        if (!body || typeof body !== 'object') {
            return null;
        }
        const isFr = this.isFrenchSelected();
        if (isFr && body?.messageFr != null && String(body.messageFr).trim()) {
            return String(body.messageFr).trim();
        }
        if (body?.messageEn != null && String(body.messageEn).trim()) {
            return String(body.messageEn).trim();
        }
        if (body?.message != null && String(body.message).trim()) {
            return String(body.message).trim();
        }
        return null;
    }

    private resolveApiErrorMessage(body: any): string {
        return this.peekApiErrorMessage(body) ?? this.translate.instant('INTERCEPTOR.GENERIC_ERROR');
    }

    /**
     * Determines if a toast message should be shown for the given error message
     * Returns false if we're on the sign-in page and the error is about invalid credentials
     */
    private shouldShowToast(errorMessage: string): boolean {
        if (!errorMessage) return true;
        
        // Check if this is a beneficiary already exists error - don't show toast for this
        if (errorMessage.toLowerCase().includes('already created as beneficiary')) {
            return false;
        }
        
        const currentRoute = this.router.url;
        const isSignInPage = currentRoute.includes('/auth/sign-in') || currentRoute.includes('/sign-in');
        
        if (!isSignInPage) return true;
        
        // Check if this is an invalid credentials error
        const isInvalidCredentials = errorMessage.toLowerCase().includes('invalid credentials') ||
                                   errorMessage.toLowerCase().includes('invalid email') ||
                                   errorMessage.toLowerCase().includes('invalid password') ||
                                   errorMessage.toLowerCase().includes('wrong password') ||
                                   errorMessage.toLowerCase().includes('incorrect password') ||
                                   errorMessage.toLowerCase().includes('authentication failed') ||
                                   errorMessage.toLowerCase().includes('login failed') ||
                                   errorMessage.toLowerCase().includes('invalid login credentials') ||
                                   errorMessage.toLowerCase().includes('your account is blocked') || errorMessage.toLowerCase().includes('already created as beneficiary')||
                                   errorMessage.toLowerCase().includes('your account is blocked') ||
                                   errorMessage.toLowerCase().includes('your account is temporary blocked.')||
                                   errorMessage.toLowerCase().includes('account locked due to multiple failed login attempts')

                                   ;
        
        // Don't show toast for invalid credentials on sign-in page
        return !isInvalidCredentials;
    }
    logout() {
        localStorage.removeItem('userToken'); // Remove token or session details
        localStorage.removeItem('userData');  // Remove stored user data
        localStorage.removeItem("interOpAliasCreated");
        this.router.navigate(["/auth/sign-in"])
    }
}



